import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/services/api";

interface UseApiRequestOptions {
  // Автоматически загружать при монтировании
  autoFetch?: boolean;
  // Кешировать результат (не делать повторный запрос если ключ не изменился)
  cache?: boolean;
}

interface UseApiRequestResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

// Глобальный кеш для запросов
const requestCache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_TTL = 60000; // 1 минута

/**
 * Хук для безопасных API запросов с защитой от дублирования
 * @param url - URL для запроса (относительный)
 * @param deps - Зависимости для перезапроса
 * @param options - Опции
 */
export function useApiRequest<T>(
  url: string | null,
  deps: unknown[] = [],
  options: UseApiRequestOptions = {}
): UseApiRequestResult<T> {
  const { autoFetch = true, cache = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs для предотвращения дублирующих запросов
  const fetchingRef = useRef(false);
  const lastUrlRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  // Создаем ключ кеша из URL и зависимостей
  const cacheKey = url ? `${url}:${JSON.stringify(deps)}` : null;

  const fetchData = useCallback(async () => {
    if (!url) return;

    // Предотвращаем дублирующие запросы
    if (fetchingRef.current) {
      return;
    }

    // Проверяем кеш
    if (cache && cacheKey) {
      const cached = requestCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setData(cached.data as T);
        return;
      }
    }

    fetchingRef.current = true;
    lastUrlRef.current = url;
    setIsLoading(true);
    setError(null);

    try {
      const resp = await api.get(url);
      const responseData: T =
        resp && (resp as any).data ? (resp as any).data : resp;

      if (mountedRef.current) {
        setData(responseData);

        // Сохраняем в кеш
        if (cache && cacheKey) {
          requestCache.set(cacheKey, {
            data: responseData,
            timestamp: Date.now(),
          });
        }
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e as Error);
        console.error(`[useApiRequest] ${url} failed:`, e);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
      fetchingRef.current = false;
    }
  }, [url, cacheKey, cache]);

  // Функция для принудительного обновления (игнорирует кеш)
  const refetch = useCallback(async () => {
    if (cacheKey) {
      requestCache.delete(cacheKey);
    }
    lastUrlRef.current = null;
    await fetchData();
  }, [fetchData, cacheKey]);

  useEffect(() => {
    mountedRef.current = true;

    if (autoFetch && url) {
      fetchData();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [url, ...deps]);

  return { data, isLoading, error, refetch };
}

/**
 * Очистить весь кеш запросов
 */
export function clearApiCache(): void {
  requestCache.clear();
}

/**
 * Очистить кеш для конкретного URL
 */
export function clearApiCacheFor(urlPattern: string): void {
  for (const key of requestCache.keys()) {
    if (key.startsWith(urlPattern)) {
      requestCache.delete(key);
    }
  }
}
